import { useState, useEffect } from 'react';
import { MapPin, History, Leaf, Users, Building, Landmark, Heart, Tractor, BookOpen } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageSEO from '../components/PageSEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const HERO_BG = "https://images.pexels.com/photos/4153968/pexels-photo-4153968.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ICON_MAP = { Tractor, Users, Building };

export const DecouvrirPage = () => {
  const [page, setPage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pages/decouvrir`).then(res => setPage(res.data)).catch(() => {});
  }, []);

  const getSection = (key) => page?.sections?.find(s => s.key === key);
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
    return url;
  };

  const COLOR_MAP = { amber: 'bg-amber-50', emerald: 'bg-emerald-50', blue: 'bg-blue-50' };
  const TEXT_COLOR_MAP = { amber: 'text-amber-600', emerald: 'text-emerald-600', blue: 'text-blue-600' };

  const historieDates = getSection('histoire_dates')?.items || [];
  const geoLocalisation = getSection('geographie_localisation')?.items || [];
  const geoDemographie = getSection('geographie_demographie')?.items || [];
  const geoEvolution = getSection('geographie_evolution')?.items || [];
  const econSecteurs = getSection('economie_secteurs')?.items || [];
  const patrimoineSites = getSection('patrimoine_sites')?.items || [];
  const cultureHarmonieItems = getSection('culture_harmonie')?.items || [];

  return (
    <main className="pt-14 md:pt-16" data-testid="decouvrir-page">
      <PageSEO
        title="Découvrir Gandiaye"
        description="Histoire, géographie, économie, patrimoine et culture de Gandiaye. Découvrez cette commune multiculturelle de la région de Kaolack, Sénégal."
        path="/decouvrir"
      />
      {/* Hero */}
      <section 
        className="relative py-24 md:py-32"
        style={{
          background: `linear-gradient(135deg, rgba(0, 166, 81, 0.92) 0%, rgba(0, 106, 78, 0.95) 100%), url(${HERO_BG}) center/cover no-repeat`
        }}
      >
        <div className="container-main">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            Découvrir Gandiaye
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Commune multiculturelle de la région de Kaolack, carrefour stratégique du Sine-Saloum.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="container-main">
          <Tabs defaultValue="histoire" className="w-full">
            <TabsList className="w-full flex flex-wrap justify-start gap-2 bg-neutral-100 p-2 rounded-xl mb-8">
              <TabsTrigger value="histoire" className="flex-1 md:flex-none" data-testid="tab-histoire">
                <History className="w-4 h-4 mr-2" />
                Histoire
              </TabsTrigger>
              <TabsTrigger value="geographie" className="flex-1 md:flex-none" data-testid="tab-geographie">
                <MapPin className="w-4 h-4 mr-2" />
                Géographie
              </TabsTrigger>
              <TabsTrigger value="economie" className="flex-1 md:flex-none" data-testid="tab-economie">
                <Building className="w-4 h-4 mr-2" />
                Économie
              </TabsTrigger>
              <TabsTrigger value="patrimoine" className="flex-1 md:flex-none" data-testid="tab-patrimoine">
                <Landmark className="w-4 h-4 mr-2" />
                Patrimoine
              </TabsTrigger>
              <TabsTrigger value="culture" className="flex-1 md:flex-none" data-testid="tab-culture">
                <Heart className="w-4 h-4 mr-2" />
                Culture
              </TabsTrigger>
            </TabsList>

            {/* Histoire */}
            <TabsContent value="histoire">
              <div className="max-w-4xl">
                {getSection('histoire')?.image_url && (
                  <img src={getImageUrl(getSection('histoire').image_url)} alt="Histoire de Gandiaye" loading="lazy" className="w-full rounded-xl mb-6" style={{ height: 'auto', objectFit: 'contain' }} />
                )}
                <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                  {getSection('histoire')?.title || 'Gandiaye, Terre d\'Accueil et de Tradition'}
                </h2>
                
                <div className="prose prose-lg text-neutral-600 space-y-6">
                  {(getSection('histoire')?.content || '').split('\n\n').filter(Boolean).map((p, i) => (
                    <p key={i} dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                  ))}

                  <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                    Origines et légendes
                  </h3>
                  
                  {getSection('histoire_legende') && (
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 rounded-xl my-6 border-l-4 border-amber-400">
                      <h4 className="font-semibold text-amber-800 mb-3">{getSection('histoire_legende').title}</h4>
                      <p className="text-neutral-700">{getSection('histoire_legende').content}</p>
                    </div>
                  )}

                  {getSection('histoire_buur') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('histoire_buur').title}
                      </h3>
                      <p>{getSection('histoire_buur').content}</p>
                    </>
                  )}

                  {historieDates.length > 0 && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('histoire_dates')?.title || 'Les grandes dates'}
                      </h3>
                      <div className="space-y-4">
                        {historieDates.map((d, i) => (
                          <div key={i} className={`flex gap-4 items-start p-4 ${COLOR_MAP[d.color] || 'bg-neutral-50'} rounded-xl`}>
                            <div className={`w-28 flex-shrink-0 font-bold ${TEXT_COLOR_MAP[d.color] || 'text-neutral-600'}`}>{d.year}</div>
                            <div>{d.text}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {getSection('histoire_personnalites') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('histoire_personnalites').title}
                      </h3>
                      <p>{getSection('histoire_personnalites').content}</p>
                    </>
                  )}

                  {getSection('histoire_administration') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('histoire_administration').title}
                      </h3>
                      <p>{getSection('histoire_administration').content}</p>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Géographie */}
            <TabsContent value="geographie">
              <div className="max-w-4xl">
                <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                  {getSection('geographie')?.title || 'Situation Géographique'}
                </h2>
                
                <div className="prose prose-lg text-neutral-600 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6 my-8">
                    <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-none">
                      <CardContent className="p-6">
                        <MapPin className="w-10 h-10 text-emerald-600 mb-4" />
                        <h4 className="font-semibold text-lg mb-2">{getSection('geographie_localisation')?.title || 'Localisation'}</h4>
                        <ul className="text-sm space-y-2 text-neutral-600">
                          {geoLocalisation.map((item, i) => (
                            <li key={i}><strong>{item.label} :</strong> {item.value}</li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-none">
                      <CardContent className="p-6">
                        <Users className="w-10 h-10 text-amber-600 mb-4" />
                        <h4 className="font-semibold text-lg mb-2">{getSection('geographie_demographie')?.title || 'Démographie (2023)'}</h4>
                        <ul className="text-sm space-y-2 text-neutral-600">
                          {geoDemographie.map((item, i) => (
                            <li key={i}><strong>{item.label} :</strong> {item.value}</li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>

                  {getSection('geographie_diversite') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('geographie_diversite').title}
                      </h3>
                      <p>{getSection('geographie_diversite').content}</p>
                    </>
                  )}

                  {getSection('geographie_carrefour') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('geographie_carrefour').title}
                      </h3>
                      <p>{getSection('geographie_carrefour').content}</p>
                    </>
                  )}

                  {geoEvolution.length > 0 && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('geographie_evolution')?.title || 'Évolution démographique'}
                      </h3>
                      <div className="grid grid-cols-3 gap-4 my-6">
                        {geoEvolution.map((item, i) => (
                          <div key={i} className={`${i === geoEvolution.length - 1 ? 'bg-emerald-100' : 'bg-neutral-100'} p-4 rounded-xl text-center`}>
                            <div className={`text-2xl font-bold ${i === geoEvolution.length - 1 ? 'text-emerald-700' : 'text-neutral-800'}`}>{item.value}</div>
                            <div className={`text-sm ${i === geoEvolution.length - 1 ? 'text-emerald-600' : 'text-neutral-500'}`}>{item.year}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {getSection('geographie_limitrophes') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('geographie_limitrophes').title}
                      </h3>
                      <p>{getSection('geographie_limitrophes').content}</p>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Économie */}
            <TabsContent value="economie">
              <div className="max-w-4xl">
                <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                  {getSection('economie')?.title || 'Le Luma de Gandiaye, Poumon Économique Régional'}
                </h2>
                
                <div className="prose prose-lg text-neutral-600 space-y-6">
                  <p>{getSection('economie')?.content}</p>

                  {econSecteurs.length > 0 && (
                    <div className="grid md:grid-cols-3 gap-6 my-8">
                      {econSecteurs.map((s, i) => {
                        const colors = ['from-emerald-50 to-emerald-100', 'from-amber-50 to-amber-100', 'from-blue-50 to-blue-100'];
                        const textColors = ['text-emerald-600', 'text-amber-600', 'text-blue-600'];
                        const IconComp = ICON_MAP[s.icon] || Building;
                        return (
                          <Card key={i} className={`bg-gradient-to-br ${colors[i % 3]} border-none`}>
                            <CardContent className="p-6 text-center">
                              <IconComp className={`w-12 h-12 ${textColors[i % 3]} mx-auto mb-4`} />
                              <h4 className="font-semibold text-lg mb-2">{s.name}</h4>
                              <p className="text-sm text-neutral-600">{s.description}</p>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  {getSection('economie_agroecologie') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        Initiatives économiques récentes
                      </h3>
                      <div className="bg-emerald-50 p-6 rounded-xl my-6">
                        <h4 className="font-semibold text-emerald-800 mb-3">{getSection('economie_agroecologie').title}</h4>
                        <p className="text-neutral-700">{getSection('economie_agroecologie').content}</p>
                      </div>
                    </>
                  )}

                  {getSection('economie_cooperative') && (
                    <div className="bg-amber-50 p-6 rounded-xl my-6">
                      <h4 className="font-semibold text-amber-800 mb-3">{getSection('economie_cooperative').title}</h4>
                      <p className="text-neutral-700">{getSection('economie_cooperative').content}</p>
                    </div>
                  )}

                  {getSection('economie_senad') && (
                    <div className="bg-blue-50 p-6 rounded-xl my-6">
                      <h4 className="font-semibold text-blue-800 mb-3">{getSection('economie_senad').title}</h4>
                      <p className="text-neutral-700">{getSection('economie_senad').content}</p>
                    </div>
                  )}

                  {getSection('economie_zone_verte') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('economie_zone_verte').title}
                      </h3>
                      <p>{getSection('economie_zone_verte').content}</p>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Patrimoine */}
            <TabsContent value="patrimoine">
              <div className="max-w-4xl">
                <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                  Patrimoine et Sites Remarquables
                </h2>
                
                <div className="prose prose-lg text-neutral-600 space-y-6">
                  {getSection('patrimoine_religieux') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800">
                        {getSection('patrimoine_religieux').title}
                      </h3>
                      <p>{getSection('patrimoine_religieux').content}</p>
                    </>
                  )}

                  {patrimoineSites.length > 0 && (
                    <div className="bg-emerald-50 p-6 rounded-xl my-8">
                      <h4 className="font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        {getSection('patrimoine_sites')?.title || 'Sites remarquables à visiter'}
                      </h4>
                      <ul className="space-y-3 text-neutral-700">
                        {patrimoineSites.map((site, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">{i + 1}</span>
                            <span><strong>{site.name}</strong> - {site.description}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {getSection('patrimoine_infrastructure') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('patrimoine_infrastructure').title}
                      </h3>
                      <p>{getSection('patrimoine_infrastructure').content}</p>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Culture */}
            <TabsContent value="culture">
              <div className="max-w-4xl">
                <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                  {getSection('culture')?.title || 'Un Modèle de Cohabitation et de Teranga'}
                </h2>
                
                <div className="prose prose-lg text-neutral-600 space-y-6">
                  <p>{getSection('culture')?.content}</p>

                  {getSection('culture_harmonie') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('culture_harmonie').title}
                      </h3>
                      <p>{getSection('culture_harmonie').content}</p>
                      
                      {cultureHarmonieItems.length > 0 && (
                        <ul className="list-disc pl-6 space-y-3">
                          {cultureHarmonieItems.map((item, i) => (
                            <li key={i}>
                              <strong>{item.title} :</strong> {item.description}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}

                  {getSection('culture_teranga') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('culture_teranga').title}
                      </h3>
                      <p>{getSection('culture_teranga').content}</p>
                    </>
                  )}

                  {getSection('culture_citation') && (
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 rounded-xl my-8 border-l-4 border-amber-400">
                      <p className="text-neutral-800 font-medium italic text-lg">
                        "{getSection('culture_citation').content}"
                      </p>
                    </div>
                  )}

                  {getSection('culture_jeunesse') && (
                    <>
                      <h3 className="text-xl font-heading font-semibold text-neutral-800 mt-8">
                        {getSection('culture_jeunesse').title}
                      </h3>
                      <p>{getSection('culture_jeunesse').content}</p>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
};

export default DecouvrirPage;
